import UniversalApiClient from './UniversalApiClient';
import ApiService from './ApiService';

// Spin Wheel Service Class (Strapi Unified Version)
// Ensures consistent handling of UniversalApiClient envelopes and raw Strapi responses.
class SpinWheelService {
  // Normalize response: Strapi may return raw { success, ... } OR UniversalApiClient may wrap as { success, data }
  static normalize(response) {
    if (!response) return { success: false, message: 'No response from server' };
    // If UniversalApiClient wrap was applied
    if (response.success && response.data && (response.data.config || response.data.result || response.data.history)) {
      return { ...response, ...response.data };
    }
    // If raw Strapi controller response
    if (response.success && (response.config || response.result || response.history)) {
      return response;
    }
    // Error style from UniversalApiClient
    if (!response.success && response.error) {
      return {
        success: false,
        status: response.status || response.error.status || 0,
        message: response.error.message || 'Request failed',
        error: response.error
      };
    }
    return response;
  }

  /** Get spin wheel configuration (segments, costs, etc.) */
  static async getWheelConfig() {
    const res = await UniversalApiClient.strapiGet('/user-actions/spin-wheel/config');
    const normalized = this.normalize(res);
    return normalized;
  }

  /** Spin the wheel */
  static async spin() {
    const res = await UniversalApiClient.strapiPost('/user-actions/spin-wheel/spin');
    const normalized = this.normalize(res);
    if (normalized.success && normalized.result) {
      // Raw spin outcome
      const raw = {
        success: true,
        message: normalized.result.message || 'Spin completed',
        pointsWon: normalized.result.pointsWon,
        newTotalPoints: normalized.result.newTotalPoints,
        segmentIndex: normalized.result.segmentIndex,
        wheelState: normalized.result.wheelState,
        spinDuration: normalized.result.spinDuration,
        remainingSpins: normalized.result.remainingSpins
      };
      return ApiService.normalizePointMutation(raw, 'spin');
    }
    return {
      success: false,
      message: normalized.message || 'Spin failed',
      data: normalized
    };
  }

  /** Restore spins using points */
  static async restoreSpins() {
    const res = await UniversalApiClient.strapiPost('/user-actions/spin-wheel/restore', {});
    const normalized = this.normalize(res);
    // New controller response shape (no result wrapper)
    if (normalized.success && (normalized.newSpinCount !== undefined || normalized.newPointsBalance !== undefined)) {
      return {
        success: true,
        message: normalized.message || 'Spins restored',
        newSpinCount: normalized.newSpinCount,
        newPointsBalance: normalized.newPointsBalance,
        spinsAdded: normalized.spinsAdded,
        costPaid: normalized.costPaid,
        maxBuyLimit: normalized.maxBuyLimit,
        buyCount: normalized.buyCount,
        remainingBuyCount: normalized.remainingBuyCount,
      };
    }
    return {
      success: false,
      message: normalized.message || 'Restore failed',
      status: normalized.status,
      buyLimitReached: normalized.buyLimitReached,
      maxBuyLimit: normalized.maxBuyLimit,
      buyCount: normalized.buyCount,
      remainingBuyCount: normalized.remainingBuyCount,
      data: normalized
    };
  }

  /** Get user's spin history */
  static async getSpinHistory(limit = 50) {
    const res = await UniversalApiClient.strapiGet('/user-actions/spin-wheel/history', { limit });
    return this.normalize(res);
  }

  /** Get spin wheel stats (if implemented) */
  static async getStats() {
    const res = await UniversalApiClient.strapiGet('/spin-wheel/stats');
    return this.normalize(res);
  }

  /** Legacy style error translator (kept for compatibility if callers still catch) */
  static handleError(error) {
    let message = 'An unexpected error occurred';
    if (error?.status) {
      const status = error.status;
      switch (status) {
        case 400: message = error.message || 'Invalid request'; break;
        case 401: message = 'Unauthorized. Please log in again.'; break;
        case 403: message = error.message || 'Access forbidden.'; break;
        case 404: message = 'Spin wheel resource not found'; break;
        case 500: message = 'Server error. Please try again later.'; break;
        default: message = error.message || `Error (${status})`;
      }
    } else if (error?.message) {
      message = error.message;
    }
    return new Error(message);
  }
}

export default SpinWheelService;
